import dayjs from 'dayjs'
import type { PrintReceiptSource } from '../types'

/**
 * `DateString` is what both renderers print as the receipt date
 * (PrintServiceV2 Graphic/ReceiptV5.cs:190, Graphic/ReceiptV2.cs). It used to be
 * persisted; derived here so a receipt that no longer stores it still prints a
 * date. Same expression the writer used, so old and new records agree.
 */
export const deriveDateString = (source: PrintReceiptSource): string => {
  if (source?.DateString) return source.DateString
  if (!source?.Date) return ''
  const parsed = dayjs(source.Date)
  return parsed.isValid() ? parsed.format('DD/MM/YYYY HH:mm:ss') : ''
}

/**
 * Mirrors `ReceiptOrderClass.applyBilling` — mode 1 is VAT inside the price,
 * mode 2 is VAT added on top. Anything else is "no VAT on this bill", which
 * prints as no VAT row at all.
 */
export const deriveVatText = (source: PrintReceiptSource): string => {
  if (source?.VatText) return source.VatText
  const mode = String(source?.VatMode ?? '')
  const value = source?.VatValue
  if (!value) return ''
  if (mode === '1') return `${value}% (Included)`
  if (mode === '2') return `${value}% (Excluded)`
  return ''
}

/**
 * The modifier SUFFIX the summary discount row appends after the word
 * "Discount" — e.g. `10%`, `*-10%*`, or empty. The caller
 * (`summarySections`) prints `Discount ${suffix}`, so this must NOT itself
 * contain the word "Discount" (doing so produced "Discount Discount 10%").
 *
 * Recomputed from `DiscountRequest`, the source of truth — never from the
 * baked `DiscountText`, which older checkouts wrote already carrying the word
 * "Discount" and a `%` even for a fixed-baht discount.
 *
 * Discount modes: 0 = percent, 1 = fixed baht, 2 = percent excluding
 * already-discounted lines. A fixed-baht discount (mode 1) has NO `%` label —
 * the row shows just "Discount" with the negative amount in the value column.
 *
 * Distinct from `displayDiscountBillText` below, the Thai form the LEGACY
 * renderer prints from `receipt.DiscountText`.
 */
export const deriveSummaryDiscountText = (source: PrintReceiptSource): string => {
  const mode = Number(source?.DiscountRequest?.mode ?? 0)
  const value = Number(source?.DiscountRequest?.value ?? 0)
  if (!(value > 0)) return ''
  if (mode === 1) return ''
  return mode === 2 ? `*-${value}%*` : `${value}%`
}

/** The `DiscountText` the legacy renderer prints. Moved from PrintReceiptNiceloopV1Builder. */
export const displayDiscountBillText = (mode = 0, value = 0): string => {
  if (value == 0) return ''
  if (mode == 0) return `ลด ${value}%`
  // mode 2 = percent excluding no-discount-tagged / already-discounted lines.
  if (mode == 2) return `*ลด ${value}%*`
  return ''
}
